import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type ReactNode } from "react";
import {
  useAdminDeviceInventoryDetail,
  useAdminDeviceInventoryInstallationChain,
} from "@/hooks/use-admin-device-inventory";

export const Route = createFileRoute("/_admin/temporary-devices/$id")({
  component: AdminDeviceInventoryDetailPage,
});

function AdminDeviceInventoryDetailPage() {
  const { t } = useTranslation(["device-inventory", "admin"]);
  const { id } = Route.useParams();
  const { data, isLoading, isError, error } = useAdminDeviceInventoryDetail(id);
  const { data: chain } = useAdminDeviceInventoryInstallationChain(id);

  if (isLoading) {
    return <p className="p-4 text-sm text-slate-500">…</p>;
  }
  if (isError) {
    return (
      <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
        {(error as Error)?.message ?? t("device-inventory:list.empty")}
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">{data.serial_number}</h1>
      </div>

      <section className="rounded-lg border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-base font-semibold text-slate-700">
          {t("device-inventory:detail.sectionIdentity")}
        </h2>
        <dl className="grid grid-cols-2 gap-4">
          <Field label={t("admin:deviceInventory.columns.orgId")}>
            <code className="text-xs text-slate-500">{data.org_id}</code>
          </Field>
          <Field label={t("device-inventory:columns.serialNumber")}>{data.serial_number}</Field>
          <Field label={t("device-inventory:columns.manufacturer")}>
            {data.manufacturer_freetext ?? data.manufacturer_norm ?? "—"}
          </Field>
          <Field label={t("device-inventory:columns.sizeInches")}>
            {data.size_inches === null || data.size_inches === undefined
              ? "—"
              : String(data.size_inches)}
          </Field>
          <Field label={t("device-inventory:fields.listingSource")}>
            {data.listing_source ?? "—"}
          </Field>
          <Field label={t("device-inventory:fields.assemblyTypeSelfDeclared")}>
            {data.assembly_type_self_declared ?? "—"}
          </Field>
        </dl>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-base font-semibold text-slate-700">
          {t("device-inventory:detail.sectionLifecycle")}
        </h2>
        <dl className="grid grid-cols-2 gap-4">
          <Field label={t("device-inventory:columns.currentInstallation")}>
            {new Date(data.first_seen_at).toLocaleString()}
          </Field>
          <Field label={t("device-inventory:columns.retiredAt")}>
            {data.retired_at ? new Date(data.retired_at).toLocaleString() : "—"}
          </Field>
          {data.retirement_reason ? (
            <Field label={t("device-inventory:retirementReason.other")}>
              {data.retirement_reason}
            </Field>
          ) : null}
        </dl>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-base font-semibold text-slate-700">
          {t("device-inventory:installationChain.title")}
        </h2>
        {chain && chain.items.length > 0 ? (
          <ul className="space-y-3">
            {chain.items.map((row) => (
              <li
                key={row.id}
                className="flex items-center justify-between rounded-md border border-slate-100 bg-slate-50 px-3 py-2 text-sm"
              >
                <span>
                  <code className="text-xs text-slate-500">{row.id.slice(0, 8)}…</code>
                  <span className="ml-2">{row.facility_name ?? "—"}</span>
                </span>
                <span className="text-xs text-slate-500">{row.device_status}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500">{t("device-inventory:installationChain.empty")}</p>
        )}
      </section>

      <Link
        to="/temporary-devices"
        search={{ org_id: undefined }}
        className="text-sm text-slate-500 hover:text-slate-700"
      >
        ← {t("device-inventory:list.title")}
      </Link>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase text-slate-500">{label}</dt>
      <dd className="mt-1 text-sm text-slate-700">{children}</dd>
    </div>
  );
}
